import React, { useEffect, useState } from 'react';
import { RESCHEDULE_FROM_ZOHO, bookableRange, getFreeWindows, slotLabel, startHours, updateLeadStatus } from '../../services/leads';
import { highestQuote, useFollowUps } from '../../services/followUps';
import './StatusUpdateSheet.css';

const OPTIONS = [
  { id: 'Cancelled', icon: '✕', title: 'Cancelled', hint: 'The customer cancelled the inspection', tone: 'cancelled' },
  { id: 'Rebook', icon: '↻', title: 'Rebook', hint: 'Needs a new time — goes on the rebook list', tone: 'moved' },
  { id: 'Reschedule', icon: '📅', title: 'Reschedule', hint: 'Pick the new date and time now', tone: 'booked' },
  { id: 'Report Sent', icon: '📄', title: 'Report already sent', hint: 'Inspection done and report sent — no visit needed', tone: 'sent' },
];

const prettyDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const windowLabel = (start) => {
  const h = start / 60;
  const fmt = (x) => `${((x + 11) % 12) + 1} ${x < 12 ? 'AM' : 'PM'}`;
  return `${fmt(h)} – ${fmt(h + 2)}`;
};

/**
 * Update Status — bottom sheet on the lead page. Everything here is the Zoho Inspection:
 *   Cancelled / Rebook — note required (signed in Zoho with the login's name)
 *   Reschedule — the agent's open 2-hour windows from the booking system (note optional)
 *   Report already sent — moves the Deal on; its amount comes from the agent's quotes (note optional)
 */
const StatusUpdateSheet = ({ lead, onClose, onSaved }) => {
  const [action, setAction] = useState(null);
  const [note, setNote] = useState('');
  const [date, setDate] = useState('');
  const [startHour, setStartHour] = useState(null);
  const [start, setStart] = useState(null); // minutes — a booking window (Zoho mode)
  const [open, setOpen] = useState({ status: 'idle', days: [], agent: '', error: '' });
  // the Deal amount is the highest quote the agent built in the app — never typed here
  const followUps = useFollowUps();
  const quoted = followUps.find((f) => f.leadId === lead.id);
  const amount = quoted && highestQuote(quoted) > 0 ? highestQuote(quoted) : null;

  useEffect(() => {
    if (action !== 'Reschedule' || !RESCHEDULE_FROM_ZOHO || open.status !== 'idle') return;
    setOpen((o) => ({ ...o, status: 'loading' }));
    getFreeWindows(lead.id)
      .then((r) => {
        const days = r.days.filter((d) => d.windows.some((w) => w.available));
        setOpen({ status: 'ready', days, agent: r.agent, error: '' });
        if (days.length) setDate(days[0].date);
      })
      .catch((err) => setOpen({ status: 'error', days: [], agent: '', error: err.message }));
  }, [action, lead.id, open.status]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const range = bookableRange();

  const needsNote = action === 'Cancelled' || action === 'Rebook';
  const ready =
    !!action &&
    (needsNote
      ? note.trim().length > 0
      : action === 'Reschedule'
        ? RESCHEDULE_FROM_ZOHO
          ? !!date && start != null
          : !!date && startHour != null && date >= range.min && date <= range.max
        : true);

  const saveLabel = !action
    ? 'Choose an option'
    : action === 'Reschedule'
      ? RESCHEDULE_FROM_ZOHO
        ? date && start != null
          ? `Reschedule to ${prettyDate(date)} · ${windowLabel(start)}`
          : 'Pick an open time'
        : date && startHour != null
          ? `Reschedule to ${prettyDate(date)} · ${slotLabel(startHour).split(' – ')[0]}`
          : 'Pick a date and time'
      : !needsNote || note.trim()
        ? `Mark as ${action}`
        : 'Add a note to save';

  const handleSave = async () => {
    if (!ready) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateLeadStatus(lead.id, { action, note, date, startHour, start, amount });
      onSaved(updated);
    } catch (err) {
      setError(err.message || 'Couldn’t update the status');
      setSaving(false);
    }
  };

  return (
    <div className="status-sheet-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="status-sheet" role="dialog" aria-label="Update status">
        <div className="status-sheet-grab" />
        <div className="status-sheet-head">
          <div>
            <h3>Update status</h3>
            <p>{lead.contactName}</p>
          </div>
          <button type="button" className="status-sheet-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="status-sheet-body">
          <div className="status-options">
            {OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                className={`status-option ${o.tone}${action === o.id ? ' selected' : ''}`}
                onClick={() => {
                  setAction(o.id);
                  setError('');
                }}
              >
                <span className="status-option-icon">{o.icon}</span>
                <span className="status-option-text">
                  <b>{o.title}</b>
                  <small>{o.hint}</small>
                </span>
                <span className="status-option-radio" />
              </button>
            ))}
          </div>

          {action === 'Reschedule' && RESCHEDULE_FROM_ZOHO && (
            <div className="status-reschedule">
              <label className="status-label">
                {open.agent ? `${open.agent}’s open times` : 'Open times'}
              </label>
              {open.status === 'loading' && <p className="status-help">Checking the calendar…</p>}
              {open.status === 'error' && (
                <p className="status-error">
                  {open.error}{' '}
                  <button type="button" className="status-retry" onClick={() => setOpen({ status: 'idle', days: [], agent: '', error: '' })}>Try again</button>
                </p>
              )}
              {open.status === 'ready' && !open.days.length && <p className="status-help">No open times in the next 3 weeks — use Rebook instead.</p>}
              {open.status === 'ready' && open.days.length > 0 && (
                <>
                  <div className="status-days">
                    {open.days.map((d) => (
                      <button
                        key={d.date}
                        type="button"
                        className={`status-day${date === d.date ? ' selected' : ''}`}
                        onClick={() => {
                          setDate(d.date);
                          setStart(null);
                        }}
                      >
                        <small>{d.weekday}</small>
                        <b>{new Date(`${d.date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</b>
                      </button>
                    ))}
                  </div>
                  <div className="status-slots">
                    {(open.days.find((d) => d.date === date) || { windows: [] }).windows.map((w) => (
                      <button
                        key={w.start}
                        type="button"
                        disabled={!w.available}
                        className={`status-slot${start === w.start ? ' selected' : ''}`}
                        onClick={() => setStart(w.start)}
                      >
                        {windowLabel(w.start)}
                        {!w.available && <small> · booked</small>}
                      </button>
                    ))}
                  </div>
                  <p className="status-help">Same open times the website shows. Tue–Sat, from tomorrow up to 3 weeks out.</p>
                </>
              )}
            </div>
          )}

          {action === 'Reschedule' && !RESCHEDULE_FROM_ZOHO && (
            <div className="status-reschedule">
              <label className="status-label" htmlFor="status-date">New date</label>
              <input
                id="status-date"
                type="date"
                className="status-date"
                min={range.min}
                max={range.max}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <p className="status-help">Earliest tomorrow, up to 3 weeks out.</p>

              <label className="status-label">New time</label>
              <div className="status-slots">
                {startHours().map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`status-slot${startHour === h ? ' selected' : ''}`}
                    onClick={() => setStartHour(h)}
                  >
                    {slotLabel(h)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {action && (
            <div className="status-note">
              <label className="status-label" htmlFor="status-note">
                Note {needsNote ? <span className="status-required">required</span> : <span className="status-optional">optional</span>}
              </label>
              <textarea
                id="status-note"
                className="status-textarea"
                rows={3}
                placeholder={
                  action === 'Cancelled'
                    ? 'Why did they cancel?'
                    : action === 'Rebook'
                      ? 'What happened, and when should we call back?'
                      : action === 'Report Sent'
                      ? 'Anything to add (optional)'
                      : 'Anything about the new time (optional)'
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          {error && <p className="status-error">{error}</p>}
        </div>

        <div className="status-sheet-foot">
          <button type="button" className="status-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={`status-save ${action ? OPTIONS.find((o) => o.id === action).tone : ''}`}
            disabled={!ready || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateSheet;
